/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as R from "remeda";
import type {
  ActionConfig,
  ActionReference,
  TriggerReference,
} from "@smartsuite/automation-engine-api";

import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import {
  getLabel,
  getOutputDescriptions,
} from "../../../../../../../../utils/conditions-coverter.hook";
import {
  isSupportedOutput,
  memoizedConvertOutputToDynamicSectionOption,
} from "../../../../utils";
import type { FieldTypePillProps } from "@smartsuite/react-ui/lib";
import { usePillTooltip } from "../../../../../../../../utils/make-tooltip.hook";
import { findMatchedAction } from "../../../../../../../../utils/match-action";
import { t } from "i18next";
import { rootStore } from "../../../../../../../../stores";
import type { LoopingOptionGroup } from "../../../../MemberEditor/utils";
import { CURRENT_ITEM_OUTPUT_ID } from "../../../../../../../../utils/constants";
import type { LoopScope } from "../../../../../../../../stores/automation/looping.store";
import { currentItemOutputsRefPill } from "../../../../utils/current-item-outputs-ref-pill";
export interface Option {
  title: string;
  icon: string;
  outputId: string;
}

export interface NumberPill extends FieldTypePillProps {
  type: "triggerOutput" | "actionOutput" | "custom" | "loop";
  value: string;
  title: string;
  icon: string;
}

export const triggerToOptions = (trigger: TriggerCandidate): Option[] =>
  getOutputDescriptions(trigger).map((output) => ({
    title: getLabel(output),
    outputId: output.outputId,
    icon: output.icon,
  }));

interface ConvertersFunctions {
  convertInputToPills: (
    trigger: TriggerCandidate,
    input: InputCandidate,
    triggerOptions: Option[],
    actionOptions: Option[],
    actions: ActionConfig[],
    actionCandidate?: ActionCandidate,
    loopOptions?: LoopingOptionGroup,
  ) => NumberPill[];
}

export const useConverters = (): ConvertersFunctions => {
  const {
    makeTriggerPillTooltip,
    makeActionPillTooltip,
    makeCurrentItemPillTooltip,
  } = usePillTooltip();

  const convertInputToPills = (
    trigger: TriggerCandidate,
    input: InputCandidate,
    triggerOptions: Option[],
    actionOptions: Option[],
    actions: ActionConfig[],
    actionCandidate?: ActionCandidate,
    loopOptions?: LoopingOptionGroup,
  ): NumberPill[] => {
    const rootLooping = rootStore.automations.candidate.looping;
    const looping = actionCandidate
      ? (rootLooping?.scopeForAction?.(actionCandidate.hash) ?? rootLooping)
      : rootLooping;
    return (
      input.value?.values.flatMap((value): NumberPill[] => {
        switch (value.value.oneofKind) {
          case "reference":
            if (value.value.reference.outputId === CURRENT_ITEM_OUTPUT_ID) {
              return [
                {
                  type: "loop",
                  value: JSON.stringify(value.value.reference),
                  icon: "",
                  title: t("general.current-item"),
                  tooltip: makeCurrentItemPillTooltip(looping),
                },
              ];
            }
            if (value.value.reference.source.oneofKind === "triggerReference") {
              const { outputId } = value.value.reference;

              let triggerOption = triggerOptions.find(
                (option) => option.outputId === outputId,
              );

              if (triggerOption === undefined && loopOptions?.options.length) {
                triggerOption = loopOptions.options.find(
                  (option) => option.outputId === outputId,
                ) as Option;
              }

              return [
                {
                  type: "triggerOutput",
                  value: outputId,
                  icon: triggerOption?.icon ?? "",
                  title:
                    triggerOption?.title ??
                    String(t("automation-form.deleted-field-title")),
                  tooltip: makeTriggerPillTooltip(trigger),
                },
              ];
            } else if (
              value.value.reference.source.oneofKind === "actionReference"
            ) {
              const { outputId } = value.value.reference;
              const actionOption = actionOptions.find(
                (option) => option.outputId === outputId,
              );

              const pillAction = findMatchedAction(
                value.value.reference.source,
                actions,
              );

              const isSupported =
                pillAction === undefined
                  ? false
                  : isSupportedOutput(
                      pillAction,
                      actions,
                      actionCandidate,
                      looping,
                    );

              if (value.value.reference.outputId.startsWith("currentItem|")) {
                const outputOptions =
                  memoizedConvertOutputToDynamicSectionOption(
                    looping,
                    rootStore.automations.candidate.allActions,
                    trigger,
                  );

                const outputOption = outputOptions?.options.find(
                  (option) => option.outputId === outputId,
                );

                return [
                  {
                    type: "actionOutput",
                    title:
                      outputOption?.label ??
                      String(t("automation-form.deleted-field-title")),
                    icon: outputOption?.icon ?? "",
                    isError: !isSupported || !outputOption?.label,
                    value: outputId,
                    tooltip:
                      pillAction === undefined
                        ? undefined
                        : makeActionPillTooltip(pillAction),
                  } as const,
                ];
              }

              return [
                {
                  type: "actionOutput",
                  value: outputId,
                  icon: actionOption?.icon ?? "",
                  title:
                    actionOption?.title ??
                    String(t("automation-form.deleted-field-title")),
                  isError: !isSupported || !actionOption?.title,
                  tooltip:
                    pillAction === undefined
                      ? undefined
                      : makeActionPillTooltip(pillAction),
                },
              ];
            } else if (
              value.value.reference.outputId === CURRENT_ITEM_OUTPUT_ID
            ) {
              return [
                {
                  type: "loop",
                  value: JSON.stringify(value.value.reference),
                  icon: "",
                  title: t("general.current-item"),
                  tooltip: makeCurrentItemPillTooltip(looping),
                },
              ];
            } else {
              return [];
            }
          case "function":
            return [];
          case "string": {
            const { string } = value.value;
            return [{ type: "custom", value: string, title: string, icon: "" }];
          }
          case "number": {
            const { number } = value.value;
            return [
              {
                type: "custom",
                value: number.toString(),
                title: number.toString(),
                icon: "",
              },
            ];
          }
          default:
            return [];
        }
      }) ?? []
    );
  };

  return {
    convertInputToPills,
  };
};

export const mergeNextPillToInput = (
  input: InputCandidate,
  pill:
    | {
        type: "triggerOutput";
        triggerReference: TriggerReference;
        outputId: string;
      }
    | {
        type: "actionOutput";
        actionReference: ActionReference;
        outputId: string;
      }
    | {
        type: "custom";
        number: string;
      }
    | { type: "loop"; looping: LoopScope },
): InputCandidate => {
  return {
    ...input,
    value: {
      values: [
        ...(R.isTruthy(input.value) ? input.value.values : []),
        ...(() => {
          switch (pill.type) {
            case "triggerOutput":
              return [
                {
                  value: {
                    oneofKind: "reference",
                    reference: {
                      source: {
                        oneofKind: "triggerReference",
                        triggerReference: pill.triggerReference,
                      },
                      outputId: pill.outputId,
                    },
                  } as const,
                },
              ];
            case "actionOutput":
              return [
                {
                  value: {
                    oneofKind: "reference",
                    reference: {
                      source: {
                        oneofKind: "actionReference",
                        actionReference: pill.actionReference,
                      },
                      outputId: pill.outputId,
                    },
                  } as const,
                },
              ];
            case "custom": {
              const value = pill.number.replace(",", ".");
              return [
                {
                  value: {
                    oneofKind: "number",
                    number: +value,
                  } as const,
                },
              ];
            }
            case "loop":
              return currentItemOutputsRefPill(pill.looping);
            default:
              return [];
          }
        })(),
      ],
    },
  };
};

export const ejectPillFromInput = (
  input: InputCandidate,
  index: number,
): InputCandidate => ({
  ...input,
  value: { values: input.value?.values.filter((_, i) => i !== index) ?? [] },
});
